import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Watchtower, { cardHasFlag } from "@/components/workflow/Watchtower";

const SEV_CHIP = { critical: "bg-red-50 text-red-700 border-red-200", high: "bg-orange-50 text-orange-700 border-orange-200", medium: "bg-amber-50 text-amber-700 border-amber-200", low: "bg-slate-50 text-slate-600 border-slate-200" };

// Payment gate status chip — shown in board card drawer header for all users.
// Contractors see this as their sole financial indicator (no dollar amounts).
// Finance-authorized users also see it alongside full invoice/estimate data.
function PaymentGateBadge({ status, testId }) {
  if (status === "confirmed") return (
    <span data-testid={testId || "wf-payment-gate-badge"} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      <Icons.CheckCircle2 className="h-3.5 w-3.5" />Payment Confirmed
    </span>
  );
  if (status === "pending") return (
    <span data-testid={testId || "wf-payment-gate-badge"} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
      <Icons.Clock className="h-3.5 w-3.5" />Payment Pending
    </span>
  );
  return null;
}

// Distinct, readable accent per stage column (cycles if more stages exist).
export const STAGE_ACCENTS = ["#64748b", "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7", "#f59e0b", "#10b981", "#22c55e", "#0ea5e9", "#ec4899", "#ef4444", "#71717a"];
const PRIORITY_DOT = { urgent: "bg-red-500", high: "bg-amber-500", normal: "bg-slate-300", low: "bg-slate-200" };

const Ico = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

function Avatar({ name, size = "h-7 w-7" }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <div className={`${size} rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[11px] font-semibold shrink-0`}>{initials}</div>;
}

function CardBadges({ b }) {
  return (
    <div className="flex items-center gap-1.5">
      {b.unread > 0 && <span title="Unread messages" className="flex items-center gap-0.5 text-[10px] text-blue-600"><Icons.MessageSquare className="h-3 w-3" />{b.unread}</span>}
      {b.documents > 0 && <span title="Outstanding documents" className="flex items-center gap-0.5 text-[10px] text-amber-600"><Icons.FileWarning className="h-3 w-3" />{b.documents}</span>}
      {b.allison > 0 && <span title={`${b.allison} Allison flag(s)`} className="flex items-center gap-0.5 text-[10px] text-violet-600"><Icons.Sparkles className="h-3 w-3" />{b.allison}</span>}
      {b.overdue && <span title="Overdue in stage"><Icons.AlarmClock className="h-3 w-3 text-red-500" /></span>}
    </div>
  );
}

// Stall-aging heat: neutral while within SLA, then warms amber → orange → red the
// longer a card sits past its stage SLA. Returns null when on track.
export function stallHeat(card) {
  const sla = card.sla_days ?? 999;
  const d = card.days_in_stage ?? 0;
  if (!sla || sla >= 999 || d <= sla) return null;
  const ratio = d / Math.max(sla, 1);
  const over = d - sla;
  if (ratio <= 1.5) return { level: "warm", bar: "#f59e0b", bg: "#fffbeb", text: "text-amber-700", over };
  if (ratio <= 2.5) return { level: "hot", bar: "#f97316", bg: "#fff7ed", text: "text-orange-700", over };
  return { level: "critical", bar: "#ef4444", bg: "#fef2f2", text: "text-red-700", over };
}

export function WorkflowCard({ card, accent, onOpen, onDragStart, dragging, showHeat = false }) {
  const heat = showHeat ? stallHeat(card) : null;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, card)}
      onClick={() => onOpen(card)}
      data-testid={`wf-card-${card.id}`}
      data-heat={heat ? heat.level : "ok"}
      style={heat ? { borderLeftWidth: 4, borderLeftColor: heat.bar, backgroundColor: heat.bg } : undefined}
      className={`group rounded-lg border border-slate-200 p-3 mb-2 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all ${heat ? "" : "bg-white"} ${dragging ? "opacity-40" : ""}`}
    >
      <div className="flex items-start gap-2">
        <Avatar name={card.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[card.priority] || PRIORITY_DOT.normal}`} title={`Priority: ${card.priority}`} />
            <span className="text-sm font-semibold text-slate-800 truncate">{card.name}</span>
          </div>
          <div className="text-[11px] text-slate-400 truncate">{card.ref}{card.service ? ` · ${card.service}` : ""}</div>
        </div>
      </div>
      {card.just_handed_off && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200" data-testid="wf-handoff-chip" title="Newly converted from a Lead — handed off by Sales within the last 24h">
          <Icons.ArrowRightLeft className="h-3 w-3" />Pushed from Sales
        </div>
      )}
      {heat && (
        <div className={`mt-2 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${heat.text}`} style={{ backgroundColor: "#ffffff", borderColor: heat.bar }} data-testid={`wf-heat-${card.id}`} title={`In stage ${card.days_in_stage}d · SLA ${card.sla_days}d`}>
          <Icons.Flame className="h-3 w-3" />+{heat.over}d over SLA
        </div>
      )}
      {card.service && <div className="mt-2"><span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-100 truncate max-w-full">{card.service}</span></div>}
      {card.badges?.payment_hold !== undefined && (
        <div className="mt-2">
          <PaymentGateBadge
            status={card.badges.payment_hold ? "pending" : "confirmed"}
            testId={card.badges.payment_hold ? "wf-badge-payment-pending" : "wf-badge-payment-confirmed"}
          />
        </div>
      )}
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
          {card.location && <span className="flex items-center gap-0.5"><Icons.MapPin className="h-3 w-3" />{card.location}</span>}
          <span className="flex items-center gap-0.5"><Icons.Clock className="h-3 w-3" />{card.days_in_stage}d</span>
        </div>
        <CardBadges b={card.badges} />
      </div>
      <div className="mt-2 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${card.progress}%`, backgroundColor: accent }} />
      </div>
    </div>
  );
}

function KpiStrip({ kpis }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="wf-kpi-strip">
      {kpis.map((k) => (
        <div key={k.key} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 min-w-[120px]" data-testid={`wf-kpi-${k.key}`}>
          <span className="h-7 w-7 rounded-md bg-slate-50 text-slate-500 flex items-center justify-center"><Ico name={k.icon} className="h-3.5 w-3.5" /></span>
          <div className="leading-tight">
            <div className="text-lg font-bold text-slate-800">{k.value}</div>
            <div className="text-[10px] text-slate-400 whitespace-nowrap">{k.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const MS_ICON = { complete: { i: "CheckCircle2", c: "text-emerald-500" }, in_progress: { i: "Clock", c: "text-amber-500" }, pending: { i: "Circle", c: "text-slate-300" }, blocked: { i: "XCircle", c: "text-red-500" }, na: { i: "MinusCircle", c: "text-slate-200" } };

function MilestoneTracker({ milestones }) {
  return (
    <div className="space-y-0" data-testid="wf-milestones">
      {milestones.map((m, i) => {
        const s = MS_ICON[m.status] || MS_ICON.pending;
        return (
          <div key={m.key} className="flex items-start gap-2.5 relative pb-3">
            {i < milestones.length - 1 && <span className="absolute left-[9px] top-5 bottom-0 w-px bg-slate-100" />}
            <Ico name={s.i} className={`h-[18px] w-[18px] mt-0.5 shrink-0 ${s.c}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm ${m.status === "complete" ? "text-slate-800 font-medium" : m.status === "in_progress" ? "text-slate-700 font-medium" : "text-slate-400"}`}>{m.label}</span>
                {m.progress && m.status !== "pending" && (
                  <span data-testid={`ms-progress-badge-${m.key}`}
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${m.status === "complete" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-700"}`}>
                    {m.status === "complete" ? "Complete" : "In Progress"} · {m.progress.done}/{m.progress.total}
                  </span>
                )}
              </div>
              {m.progress && m.status !== "pending" && (
                <div className="mt-1.5 w-40 h-1.5 rounded-full bg-slate-100 overflow-hidden" data-testid={`ms-progress-bar-${m.key}`}>
                  <div className={`h-full rounded-full transition-[width] duration-500 ${m.status === "complete" ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${m.progress.percent}%` }} />
                </div>
              )}
              {m.status === "complete" && <div className="text-[11px] text-slate-400 mt-0.5">{m.date ? new Date(m.date).toLocaleDateString() : ""}{m.actor ? ` · ${m.actor}` : ""}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const DRAWER_TABS = ["Overview", "Allison", "Milestones", "Timeline"];

function AllisonTab({ allison, cardId, cardEndpointBase }) {
  const [asking, setAsking] = useState(false);
  const [notes, setNotes] = useState(allison?.notes || []);
  const flags = allison?.flags || [];
  const ask = async () => {
    setAsking(true);
    try {
      const r = await api.post(`${cardEndpointBase}/${cardId}/ask-allison`, {});
      setNotes((n) => [r.data.note, ...n]);
      if (r.data.source !== "llm") toast.info("Allison replied from its fallback (LLM unavailable).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Allison is unavailable right now.");
    }
    setAsking(false);
  };
  return (
    <div className="space-y-4" data-testid="wf-drawer-allison">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Icons.ShieldAlert className="h-3.5 w-3.5" />Operational Flags ({flags.length})</div>
        {flags.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2"><Icons.CheckCircle2 className="h-4 w-4" />No operational issues detected.</div>
        ) : (
          <div className="space-y-2">
            {flags.map((f) => (
              <div key={f.key} className={`rounded-lg border p-2.5 ${SEV_CHIP[f.severity] || SEV_CHIP.low}`} data-testid={`wf-flag-${f.key}`}>
                <div className="flex items-center gap-1.5 text-sm font-semibold"><Ico name={f.icon} className="h-3.5 w-3.5" />{f.label}<span className="ml-auto text-[10px] uppercase font-bold opacity-70">{f.severity}</span></div>
                <div className="text-[12px] mt-1 flex items-start gap-1 opacity-90"><Icons.ArrowRight className="h-3 w-3 mt-0.5 shrink-0" />{f.recommendation}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <button onClick={ask} disabled={asking} data-testid="wf-ask-allison" className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 transition-colors">
        {asking ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Sparkles className="h-4 w-4" />}{asking ? "Allison is thinking…" : "Ask Allison"}
      </button>
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Allison Notes</div>
        {notes.length === 0 ? <div className="text-[12px] text-slate-400">No Allison reviews yet. Click “Ask Allison” for an on-demand operational review.</div> : (
          <div className="space-y-2.5">
            {notes.map((n) => (
              <div key={n.id} className="bg-violet-50/60 border border-violet-100 rounded-lg p-3" data-testid={`wf-allison-note-${n.id}`}>
                <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{n.text}</div>
                <div className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1"><Icons.Sparkles className="h-3 w-3 text-violet-400" />{n.source === "llm" ? "Allison (Claude)" : "Allison (fallback)"}{n.created_at ? ` · ${new Date(n.created_at).toLocaleString()}` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function DetailDrawer({ cardId, onClose, cardEndpointBase = "/workflow/card", workspacePathFn }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Overview");
  useEffect(() => {
    if (!cardId) return;
    setData(null); setTab("Overview");
    api.get(`${cardEndpointBase}/${cardId}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [cardId, cardEndpointBase]);
  if (!cardId) return null;
  const o = data?.overview;
  return (
    <>
      <div className="fixed inset-0 bg-slate-900/20 z-40" onClick={onClose} data-testid="wf-drawer-overlay" />
      <div className="fixed top-0 right-0 h-full w-full max-w-[440px] bg-white z-50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200" data-testid="wf-drawer">
        {!data ? <div className="flex-1 flex items-center justify-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div> : data === false ? (
          <div className="p-6 text-sm text-slate-400">Could not load details.</div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-100 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Avatar name={o.name} size="h-11 w-11" />
                <div>
                  <div className="text-base font-bold text-slate-800">{o.name}</div>
                  <div className="text-xs text-slate-400">{o.ref}{o.service ? ` · ${o.service}` : ""}</div>
                </div>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="wf-drawer-close"><Icons.X className="h-5 w-5" /></button>
            </div>
            {workspacePathFn && (
              <a href={workspacePathFn(data.workspace_id || cardId)} data-testid="wf-drawer-open-workspace"
                className="mx-4 mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                <Icons.ExternalLink className="h-3.5 w-3.5" />Open Detail Page
              </a>
            )}
            <div className="px-4 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] text-slate-400">Health</span>
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${o.health}%` }} /></div>
                <span className="text-[11px] font-medium text-slate-600">{o.health}%</span>
              </div>
              {data.payment_gate_status && (
                <div className="mb-3" data-testid="wf-payment-gate-container">
                  <PaymentGateBadge status={data.payment_gate_status} testId={`wf-payment-gate-${cardId}`} />
                </div>
              )}
              <div className="flex gap-1 overflow-x-auto border-b border-slate-100">
                {DRAWER_TABS.map((t) => (
                  <button key={t} onClick={() => setTab(t)} data-testid={`wf-drawer-tab-${t}`}
                    className={`px-2.5 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px ${tab === t ? "border-blue-500 text-blue-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {tab === "Overview" && (
                <div className="space-y-2 text-sm">
                  {[["Email", o.email], ["Phone", o.phone], ["Location", o.location], ["Source", o.source], ["Team", o.team], ["Assigned", o.staff], ["Priority", o.priority], ["Stage", o.stage]].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 border-b border-slate-50 py-1.5"><span className="text-slate-400">{k}</span><span className="text-slate-700 text-right truncate">{v || "—"}</span></div>
                  ))}
                  {data.next_actions?.length > 0 && (
                    <div className="pt-3">
                      <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Next Action</div>
                      {data.next_actions.map((n) => (
                        <a key={n.milestone} href={n.link || "#"} data-testid={`wf-next-action-${n.milestone}`}
                          className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline py-1.5">
                          <Icons.ArrowRight className="h-3.5 w-3.5" />{n.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {tab === "Allison" && <AllisonTab allison={data.allison} cardId={cardId} cardEndpointBase={cardEndpointBase} />}
              {tab === "Milestones" && <MilestoneTracker milestones={data.milestones} />}
              {tab === "Timeline" && (
                <div className="space-y-2.5">
                  {data.timeline.length === 0 ? <p className="text-sm text-slate-400">No activity yet.</p> : data.timeline.map((t, i) => (
                    <div key={i} className="flex items-start gap-2.5"><Icons.Dot className="h-4 w-4 text-slate-300 mt-0.5" /><div><div className="text-sm text-slate-700">{t.title}</div><div className="text-[11px] text-slate-400">{t.date ? new Date(t.date).toLocaleString() : ""}{t.actor ? ` · ${t.actor}` : ""}</div></div></div>
                  ))}
                </div>
              )}
              {tab === "Estimates" && <ListBlock rows={data.estimates} label="estimates" render={(e) => `${e.ref || "Estimate"} · ${e.status} · $${e.total ?? "—"}`} />}
              {tab === "Engagement" && (data.engagement ? <div className="text-sm text-slate-700">{data.engagement.ref} · {data.engagement.status}</div> : <p className="text-sm text-slate-400">No engagement yet.</p>)}
              {tab === "Invoices" && <ListBlock rows={data.invoices} label="invoices" render={(i) => `${i.ref || "Invoice"} · ${i.status} · $${i.total ?? "—"}`} />}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ListBlock({ rows, label, render }) {
  if (!rows?.length) return <p className="text-sm text-slate-400">No {label} yet.</p>;
  return <div className="space-y-1.5">{rows.map((r) => <div key={r.id} className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">{render(r)}</div>)}</div>;
}

export default function WorkflowBoard({ boardKey = "lead", title, classicPath, boardEndpoint, cardEndpointBase = "/workflow/card", transitionFn, workspacePathFn, subtitle }) {
  const [board, setBoard] = useState(null);
  const [drag, setDrag] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [undo, setUndo] = useState(null);
  const [f, setF] = useState({ q: "", team: "", service: "", priority: "", attention: false });
  const [params, setParams] = useSearchParams();
  const [focusStage, setFocusStage] = useState(null);
  const [flagFilter, setFlagFilter] = useState(null); // { key, label }
  useEffect(() => {
    const st = params.get("stage"), fl = params.get("filter"), qq = params.get("q"), flag = params.get("flag");
    if (fl === "attention" || fl === "overdue" || fl === "blocked") setF((p) => ({ ...p, attention: true }));
    if (qq) setF((p) => ({ ...p, q: qq }));
    if (st) setFocusStage(st);
    if (flag) setFlagFilter({ key: flag, label: flag.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const focusRef = useCallback((el) => { if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }); }, []);
  const clearFocus = () => { setFocusStage(null); const np = new URLSearchParams(params); np.delete("stage"); setParams(np, { replace: true }); };
  const applyFlag = (row) => { setFlagFilter({ key: row.key, label: row.label }); const np = new URLSearchParams(params); np.set("flag", row.key); setParams(np, { replace: true }); };
  const clearFlag = () => { setFlagFilter(null); const np = new URLSearchParams(params); np.delete("flag"); setParams(np, { replace: true }); };

  const endpoint = boardEndpoint || `/workflow/board/${boardKey}`;
  const load = useCallback(() => api.get(endpoint).then((r) => setBoard(r.data)).catch(() => setBoard(false)), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!board) return [];
    return board.cards.filter((c) => {
      if (f.q && !(`${c.name} ${c.ref} ${c.service || ""}`.toLowerCase().includes(f.q.toLowerCase()))) return false;
      if (f.team && c.team !== f.team) return false;
      if (f.service && c.service !== f.service) return false;
      if (f.priority && c.priority !== f.priority) return false;
      if (f.attention && !(c.badges.unread || c.badges.documents || c.badges.payment_hold || c.badges.overdue || c.badges.allison)) return false;
      if (flagFilter && !cardHasFlag(c, flagFilter.key)) return false;
      return true;
    });
  }, [board, f, flagFilter]);

  const doTransition = async (card, toStage, method = "drag_drop", silentUndo = false) => {
    const from = card.stage;
    if (from === toStage) return;
    // optimistic
    setBoard((b) => ({ ...b, cards: b.cards.map((c) => c.id === card.id ? { ...c, stage: toStage } : c) }));
    try {
      const r = transitionFn
        ? await transitionFn({ card, toStage, method, board })
        : await api.post("/workflow/transition", { entity_id: card.id, pipeline_key: board.pipeline.key, to_stage: toStage, method });
      const percent = r?.data?.percent ?? r?.percent;
      setBoard((b) => ({ ...b, cards: b.cards.map((c) => c.id === card.id ? { ...c, stage: toStage, progress: percent ?? c.progress, days_in_stage: 0 } : c) }));
      if (!silentUndo) {
        const label = board.columns.find((s) => s.key === toStage)?.label || toStage;
        setUndo({ card: { ...card, stage: toStage }, from, label });
        setTimeout(() => setUndo((u) => (u && u.card.id === card.id ? null : u)), 15000);
      }
    } catch (e) {
      setBoard((b) => ({ ...b, cards: b.cards.map((c) => c.id === card.id ? { ...c, stage: from } : c) }));
      toast.error(e.response?.data?.detail || "Move blocked", { description: "The card was returned to its original stage." });
    }
  };

  const onDrop = (stageKey) => { if (drag) { doTransition(drag, stageKey); setDrag(null); } };
  const runUndo = () => { if (undo) { doTransition(undo.card, undo.from, "undo", true); setUndo(null); } };

  if (board === false) return <div className="p-6 text-sm text-red-500">Failed to load workflow board.</div>;
  if (!board) return <div className="p-10 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500 inline" /></div>;

  return (
    <div className="p-5 space-y-3 h-full flex flex-col" data-testid="workflow-board">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title || board.pipeline.name}</h1>
          <p className="text-sm text-slate-500">{subtitle || "One continuous lifecycle · drag cards to advance stages"}</p>
        </div>
        {classicPath && <a href={classicPath} className="text-xs text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5" data-testid="wf-classic-view"><Icons.LayoutDashboard className="h-3.5 w-3.5" />Classic View</a>}
      </div>

      <KpiStrip kpis={board.kpis} />
      {board.watchtower && <Watchtower data={board.watchtower} onRowClick={applyFlag} />}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Icons.Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-2.5" /><input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search…" className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg w-52" data-testid="wf-filter-search" /></div>
        <select value={f.team} onChange={(e) => setF({ ...f, team: e.target.value })} className="text-sm border border-slate-200 rounded-lg px-2 py-1.5" data-testid="wf-filter-team"><option value="">All teams</option>{board.filters.teams.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <select value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} className="text-sm border border-slate-200 rounded-lg px-2 py-1.5" data-testid="wf-filter-service"><option value="">All services</option>{board.filters.services.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="text-sm border border-slate-200 rounded-lg px-2 py-1.5" data-testid="wf-filter-priority"><option value="">Any priority</option>{["urgent", "high", "normal", "low"].map((p) => <option key={p} value={p}>{p}</option>)}</select>
        <button onClick={() => setF({ ...f, attention: !f.attention })} className={`text-sm rounded-lg px-3 py-1.5 border flex items-center gap-1.5 ${f.attention ? "bg-amber-50 border-amber-300 text-amber-700" : "border-slate-200 text-slate-500"}`} data-testid="wf-filter-attention"><Icons.AlertTriangle className="h-3.5 w-3.5" />Needs attention</button>
        {focusStage && <button onClick={clearFocus} data-testid="wf-focus-chip" className="text-sm rounded-lg px-3 py-1.5 border bg-blue-50 border-blue-300 text-blue-700 flex items-center gap-1.5"><Icons.Crosshair className="h-3.5 w-3.5" />Focused: {board.columns.find((c) => c.key === focusStage)?.label || focusStage}<Icons.X className="h-3.5 w-3.5" /></button>}
        {flagFilter && <button onClick={clearFlag} data-testid="wf-flag-chip" className="text-sm rounded-lg px-3 py-1.5 border bg-violet-50 border-violet-300 text-violet-700 flex items-center gap-1.5"><Icons.Sparkles className="h-3.5 w-3.5" />Allison: {flagFilter.label}<Icons.X className="h-3.5 w-3.5" /></button>}
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} of {board.cards.length}</span>
      </div>

      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-3 h-full min-w-max pb-2">
          {board.columns.map((col, idx) => {
            const accent = STAGE_ACCENTS[idx % STAGE_ACCENTS.length];
            const colCards = filtered.filter((c) => c.stage === col.key);
            const breached = colCards.filter((c) => c.days_in_stage > (col.sla_days ?? 999)).length;
            return (
              <div key={col.key} ref={focusStage === col.key ? focusRef : undefined} className={`w-[272px] shrink-0 flex flex-col rounded-xl transition-colors ${focusStage === col.key ? "bg-blue-50 ring-2 ring-blue-400" : "bg-slate-50/70"}`} data-testid={`wf-column-${col.key}`}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(col.key)}>
                <div className={`px-3 py-2.5 flex items-center justify-between rounded-t-xl border-t-2 transition-colors ${breached ? "bg-amber-50" : ""}`}
                  style={{ borderColor: breached ? "#f59e0b" : accent }}
                  title={breached ? `${breached} card(s) past the ${col.sla_days}-day SLA for this stage` : `SLA: ${col.sla_days} days`}
                  data-testid={`wf-column-header-${col.key}`}>
                  <span className={`text-xs font-semibold uppercase tracking-wide truncate flex items-center gap-1 ${breached ? "text-amber-700" : "text-slate-700"}`}>
                    {breached > 0 && <span className="relative flex h-2 w-2" data-testid={`wf-sla-pulse-${col.key}`}><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" /></span>}
                    {col.label}
                  </span>
                  <span className="flex items-center gap-1">
                    {breached > 0 && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5" title="Cards past SLA">{breached} ⚠</span>}
                    <span className="text-[11px] font-medium text-slate-400 bg-white rounded-full px-2 py-0.5">{colCards.length}</span>
                  </span>
                </div>
                <div className="px-2 pb-2 flex-1 overflow-y-auto min-h-[120px]">
                  {colCards.map((c) => <WorkflowCard key={c.id} card={c} accent={accent} showHeat onOpen={(cd) => setOpenId(cd.id)} onDragStart={(e, cd) => setDrag(cd)} dragging={drag?.id === c.id} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* badge legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-400 border-t border-slate-100 pt-2">
        <span className="flex items-center gap-1"><Icons.MessageSquare className="h-3 w-3 text-blue-600" />Unread</span>
        <span className="flex items-center gap-1"><Icons.FileWarning className="h-3 w-3 text-amber-600" />Docs outstanding</span>
        <span className="flex items-center gap-1"><Icons.CheckCircle2 className="h-3 w-3 text-emerald-500" />Payment Confirmed</span>
        <span className="flex items-center gap-1"><Icons.Clock className="h-3 w-3 text-amber-600" />Payment Pending</span>
        <span className="flex items-center gap-1"><Icons.Sparkles className="h-3 w-3 text-violet-500" />Allison rec</span>
        <span className="flex items-center gap-1"><Icons.AlarmClock className="h-3 w-3 text-red-500" />Overdue</span>
        <span className="flex items-center gap-1 pl-2 border-l border-slate-100"><Icons.Flame className="h-3 w-3 text-amber-500" />Stall aging:</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />Past SLA</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-orange-500" />Stalling</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-500" />Severe</span>
      </div>

      {openId && <DetailDrawer cardId={openId} onClose={() => setOpenId(null)} cardEndpointBase={cardEndpointBase} workspacePathFn={workspacePathFn} />}

      {undo && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-xl px-4 py-3 flex items-center gap-3 shadow-xl animate-in slide-in-from-bottom" data-testid="wf-undo-toast">
          <Icons.CheckCircle2 className="h-4 w-4 text-emerald-400" />
          <span className="text-sm">Moved <b>{undo.card.name}</b> to {undo.label}</span>
          <button onClick={runUndo} className="text-sm font-semibold text-blue-300 hover:text-blue-200" data-testid="wf-undo-btn">Undo</button>
        </div>
      )}
    </div>
  );
}
